import React from 'react';
import { LuCircleAlert, LuRefreshCw } from 'react-icons/lu';

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

/**
 * Catches render-time errors (and failed lazy-chunk loads) so one broken page
 * shows a recovery screen instead of a blank app. Pass `resetKey` (e.g. the
 * current pathname) to clear the error automatically when the user navigates.
 */
class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Unhandled UI error:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A new deployment replaced the chunk this page needs — a reload fetches it.
    const chunkError = isChunkLoadError(error);

    return (
      <div className={`state-block state-block--error ${this.props.fullPage ? 'state-block--page' : ''}`} role="alert" style={this.props.fullPage ? { minHeight: '100vh' } : undefined}>
        <span className="state-block__icon"><LuCircleAlert aria-hidden="true" /></span>
        <p className="state-block__title">{chunkError ? 'A new version is available' : 'Something went wrong'}</p>
        <p className="state-block__text">
          {chunkError
            ? 'This page could not be loaded because the portal was updated. Reload to continue.'
            : 'This page hit an unexpected error. You can try again, or reload the portal.'}
        </p>
        <div className="state-block__actions">
          {!chunkError && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
            <LuRefreshCw aria-hidden="true" /> Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
